import { HardHat, Mic, Search, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import { normalizarBusca } from '@/nucleo/utilitarios';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/ui/base/command';
import { DialogDescription, DialogTitle } from '@/ui/base/dialog';

import { ehSoDre } from './apresentacao';
import {
  gruposVisiveis,
  veDocumentacaoPreCredenciamento,
  type AreaDeNavegacao,
  type ItemDeNavegacao,
} from './mapa';

/**
 * Botão da busca global, mais o diálogo que ele abre.
 *
 * Busca por TELA (navegação): filtra os itens do menu da área atual, a mesma
 * fonte que alimenta a barra lateral (`mapa.ts`), agrupados do mesmo jeito,
 * sem lista paralela. Busca por CREDENCIADO, PLACA ou ORDEM DE SERVIÇO ainda
 * não existe, porque depende de indexação no backend que não foi construída
 * nesta história — o estado vazio (sem resultado pro que foi digitado) diz
 * isso, em vez de fingir.
 *
 * Cmd+K (Mac) ou Ctrl+K (Windows/Linux) abrem de qualquer rota interna, já que
 * este componente está na casca de toda área.
 *
 * Esc fecha e devolve o foco pro botão que abriu. Não é de graça: o
 * `onCloseAutoFocus` do Radix só devolve foco sozinho pra quem é
 * `DialogTrigger` de verdade, e aqui há dois botões próprios (desktop e
 * mobile) fora dele, sem isso o foco caía em `<body>`. Medido com
 * `document.activeElement` de verdade (Playwright), não só pela aparência.
 */
export function BuscaGlobal({ area }: { area: AreaDeNavegacao }) {
  const [aberto, setAberto] = useState(false);
  const navegar = useNavigate();
  const { usuario } = useAuth();
  // Elemento a devolver o foco quando o diálogo fechar: o botão clicado, ou,
  // no atalho de teclado, o que estava com foco no momento do Cmd/Ctrl+K.
  const elementoAnteriorRef = useRef<HTMLElement | null>(null);

  function abrir(evento: React.MouseEvent<HTMLButtonElement>) {
    elementoAnteriorRef.current = evento.currentTarget;
    setAberto(true);
  }

  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key.toLowerCase() === 'k' && (evento.metaKey || evento.ctrlKey)) {
        evento.preventDefault();
        if (document.activeElement instanceof HTMLElement) {
          elementoAnteriorRef.current = document.activeElement;
        }
        setAberto((atual) => !atual);
      }
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  function irPara(caminho: string) {
    setAberto(false);
    navegar(caminho);
  }

  const grupos = gruposVisiveis(
    area,
    usuario?.papeis,
    usuario?.credenciadoTipo,
    veDocumentacaoPreCredenciamento(usuario),
  );
  const itensVisiveis = grupos.flatMap((grupo) =>
    grupo.itens.flatMap((item) => [item, ...(item.filhos ?? [])]),
  );

  // Aponta pra tela real mais próxima do que seria a ação do protótipo (ex.:
  // a lista de "Ordens de serviço", não "Nova ordem de serviço" — criar
  // ainda não existe). Sem atalhosRapidos configurado, a área simplesmente
  // não tem essa seção, em vez de inventar destino. O atalho sai dos MESMOS
  // itens visíveis da lista abaixo: atalho para tela que o papel não abre
  // levaria à guarda de rota (regra 7 de interface).
  const atalhosRapidos = (area.atalhosRapidos ?? [])
    .map((caminho) => itensVisiveis.find((item) => item.caminho === caminho))
    .filter((item): item is ItemDeNavegacao => item !== undefined);

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        className="hidden h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex"
        aria-label="Buscar (⌘K)"
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate text-left">Buscar credenciado, placa, OS...</span>
        <kbd className="inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      {/*
        Só existe em telas estreitas: ocupa o `flex-1` que o botão de cima tem
        no desktop, empurrando sino/tema/conta pra direita também no mobile.
        Sem isso, os ícones ficam grudados à esquerda (mesmo espaçador do
        protótipo).
      */}
      <div className="flex-1 sm:hidden" aria-hidden="true" />

      <button
        type="button"
        onClick={abrir}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:hidden"
        aria-label="Buscar (⌘K)"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
      </button>

      <CommandDialog
        open={aberto}
        onOpenChange={setAberto}
        onCloseAutoFocus={(evento) => {
          evento.preventDefault();
          elementoAnteriorRef.current?.focus();
        }}
        filter={(valor, busca) => (normalizarBusca(valor).includes(normalizarBusca(busca)) ? 1 : 0)}
        className="top-[10vh] translate-y-0 rounded-2xl border-border/60 shadow-2xl sm:max-w-2xl"
        commandClassName="**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:pt-3 **:[[cmdk-group-heading]]:pb-1.5 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:font-bold **:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wider **:[[cmdk-group-heading]]:text-primary/80"
      >
        <DialogTitle className="sr-only">Busca global</DialogTitle>
        <DialogDescription className="sr-only">
          Busque uma tela do menu de {area.nome} por nome. Busca por credenciado, placa e ordem de
          serviço chega em breve.
        </DialogDescription>
        <div className="flex items-center gap-2 border-b border-border/60 px-4 pb-3 pt-4">
          <CommandInput
            placeholder="Buscar credenciado, placa, OS..."
            wrapperClassName="flex-1 rounded-xl border border-border/60 bg-background px-3 h-12 focus-within:ring-2 focus-within:ring-ring focus-within:border-ring transition-shadow"
            rightSlot={
              <button
                type="button"
                disabled
                aria-label="Busca por voz (em breve)"
                className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground/50 disabled:cursor-not-allowed"
              >
                <Mic className="h-4 w-4" aria-hidden="true" />
              </button>
            }
          />
          <button
            type="button"
            onClick={() => setAberto(false)}
            aria-label="Fechar busca"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <CommandList className="max-h-[60vh] sm:max-h-[480px]">
          <CommandEmpty>
            Nenhuma tela encontrada. Busca por credenciado, placa e ordem de serviço chega em breve.
          </CommandEmpty>

          {atalhosRapidos.length > 0 && (
            <>
              <CommandGroup heading="Ações rápidas">
                {atalhosRapidos.map((item) => (
                  <ItemDeBusca key={`atalho-${item.caminho}`} item={item} aoEscolher={irPara} />
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {grupos.map((grupo, indice) => (
            <Fragment key={grupo.titulo}>
              {indice > 0 && <CommandSeparator />}
              <CommandGroup heading={grupo.titulo}>
                {grupo.itens
                  .flatMap((item) => [item, ...(item.filhos ?? [])])
                  .map((item) => (
                    <ItemDeBusca key={item.caminho} item={item} aoEscolher={irPara} />
                  ))}
              </CommandGroup>
            </Fragment>
          ))}
        </CommandList>

        <div className="flex items-center justify-between border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">↑↓</kbd>
              navegar
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">↵</kbd>
              abrir
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">esc</kbd>
              fechar
            </span>
          </div>
          <span className="flex items-center gap-1.5 text-muted-foreground/60">
            <Mic className="h-3 w-3" aria-hidden="true" />
            busca por voz
          </span>
        </div>
      </CommandDialog>
    </>
  );
}

function ItemDeBusca({
  item,
  aoEscolher,
}: {
  item: ItemDeNavegacao;
  aoEscolher: (caminho: string) => void;
}) {
  const Icone = item.icone;
  const emConstrucao = item.fase > 1;
  const { usuario } = useAuth();
  const rotulo = item.tituloParaDre && ehSoDre(usuario?.papeis) ? item.tituloParaDre : item.titulo;

  return (
    <CommandItem
      value={`${rotulo} ${item.resumo ?? ''}`.trim()}
      onSelect={() => aoEscolher(item.caminho)}
      className="mx-2 rounded-lg aria-selected:bg-accent"
    >
      <Icone className="mr-3 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="flex-1 truncate text-foreground">{rotulo}</span>
      {emConstrucao && (
        <>
          <HardHat className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">— módulo da fase {item.fase}</span>
        </>
      )}
    </CommandItem>
  );
}
